import React, { ButtonHTMLAttributes, ReactNode } from 'react';

import { classNames } from '@repo/ui/src/utils/classNames';
import SkewerIcon from '@repo/ui/src/components/Icons/SkewerIcon';
import KnifeIcon from '@repo/ui/src/components/Icons/KnifeIcon';

interface Base {
  children: ReactNode;
}

export enum ButtonVariant {
  primary = 'primary',
  secondary = 'secondary',
  tertiary = 'tertiary',
  knife = 'knife',
  skewer = 'skewer',
  bare = 'bare',
}
export interface BaseButtonProps extends Base {
  className?: string;
  variant?: ButtonVariant;
  active?: boolean;
  type?: 'submit' | 'reset' | 'button';
  size?: 'large' | 'medium' | 'small' | 'none';
  full?: boolean;
  as?: React.ComponentType<any>;
  dark?: boolean;
  negative?: boolean;
}
export type ButtonProps<ComponentType = 'button'> = (ComponentType extends React.ComponentType<
  infer PropsType
>
  ? PropsType
  : ButtonHTMLAttributes<HTMLButtonElement>) &
  BaseButtonProps & { as?: ComponentType };
type ButtonType = <T>(props: ButtonProps<T>) => JSX.Element;

const Button: ButtonType = ({
  className,
  variant = ButtonVariant.primary,
  size = 'medium',
  children,
  active,
  as: Component = 'button',
  full,
  dark = false,
  negative = false,
  ...rest
}) => {
  const special = variant === 'knife' || variant === 'skewer';
  return (
    <Component
      aria-pressed={active}
      data-variant={variant}
      className={classNames(
        'tra-text-web-utl-button rounded group inline-block cursor-pointer',
        variant === 'primary' && 'tra-btn-primary',
        variant === 'secondary' && 'tra-btn-secondary',
        variant === 'tertiary' && 'tra-btn-tertiary',
        variant === 'bare' && 'tra-btn-bare',
        (variant === 'skewer' || variant === 'knife') && 'grid grid-flow-col items-center',
        special && 'tra-btn-special !px-0',
        size === 'large' && 'py-4 px-12 tra-text-web-utl-button-l',
        size === 'medium' && 'py-3 px-10 tra-text-web-utl-button-m',
        size === 'small' && 'py-1 px-6 tra-text-web-utl-button-s',
        size === 'small' && variant === 'primary' && 'font-bold',
        special && dark && 'text-white',
        full ? 'w-full' : 'w-fit',
        negative && 'negative',
        // @ts-expect-error disabled might not exist
        rest.disabled && Component !== 'button' && 'tra-disabled',
        className,
      )}
      {...rest}
    >
      {children}
      {variant === 'skewer' && (
        <SkewerIcon className="tra-btn-icon-special text-brand-300" aria-hidden="true" />
      )}
      {variant === 'knife' && (
        <KnifeIcon className="tra-btn-icon-special text-brand-300" aria-hidden="true" />
      )}
    </Component>
  );
};

export default Button;
